import {
  createCameraState,
  createGeoBounds,
  createLngLat,
  type CameraState,
  type GeoBounds,
  type LngLat,
} from './coordinates.ts';

export const maximumPitch = 60;

const earthCircumference = 40_075_016.686;
const tileSize = 512;
const cameraFactor = 1.5;
const maximumMercatorLatitude = 85.051_128_78;
const initialMaximumZoom = 12;
const initialPadding = Object.freeze({ bottom: 60, left: 30, right: 30, top: 80 });

export interface ViewportSize {
  readonly height: number;
  readonly width: number;
}

function expandBounds(bounds: GeoBounds, factor: number): GeoBounds {
  const centerLongitude = (bounds.southWest[0] + bounds.northEast[0]) / 2;
  const centerLatitude = (bounds.southWest[1] + bounds.northEast[1]) / 2;
  const halfWidth = ((bounds.northEast[0] - bounds.southWest[0]) / 2) * factor;
  const halfHeight = ((bounds.northEast[1] - bounds.southWest[1]) / 2) * factor;
  const rounded = (value: number): number => Number(value.toFixed(6));
  return createGeoBounds(
    createLngLat(rounded(centerLongitude - halfWidth), rounded(centerLatitude - halfHeight)),
    createLngLat(rounded(centerLongitude + halfWidth), rounded(centerLatitude + halfHeight)),
  );
}

const zugerbergTrailBounds = createGeoBounds(
  createLngLat(8.52148, 47.1433),
  createLngLat(8.53276, 47.14991),
);

const cantonZugBounds = createGeoBounds(createLngLat(8.396, 47.1), createLngLat(8.682, 47.23));

export const initialMapBounds = expandBounds(zugerbergTrailBounds, 8);
export const maximumMapBounds = expandBounds(cantonZugBounds, 5);

function requireFinite(value: number, name: string): number {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function requirePositive(value: number, name: string): number {
  requireFinite(value, name);
  if (value <= 0) throw new RangeError(`${name} must be greater than zero`);
  return value;
}

function requireRange(value: number, minimum: number, maximum: number, name: string): number {
  requireFinite(value, name);
  if (value < minimum || value > maximum) {
    throw new RangeError(`${name} must be between ${String(minimum)} and ${String(maximum)}`);
  }
  return value;
}

function mercatorY(latitude: number): number {
  const radians = (latitude * Math.PI) / 180;
  return (1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2;
}

export function initialCameraForViewport(viewport: ViewportSize): CameraState {
  const width = requirePositive(viewport.width, 'viewport width');
  const height = requirePositive(viewport.height, 'viewport height');
  const availableWidth = requirePositive(
    width - initialPadding.left - initialPadding.right,
    'padded viewport width',
  );
  const availableHeight = requirePositive(
    height - initialPadding.top - initialPadding.bottom,
    'padded viewport height',
  );
  const longitudeFraction = (initialMapBounds.northEast[0] - initialMapBounds.southWest[0]) / 360;
  const latitudeFraction =
    mercatorY(initialMapBounds.southWest[1]) - mercatorY(initialMapBounds.northEast[1]);
  const horizontalZoom = Math.log2(availableWidth / (tileSize * longitudeFraction));
  const verticalZoom = Math.log2(availableHeight / (tileSize * latitudeFraction));

  return createCameraState({
    center: createLngLat(
      (initialMapBounds.southWest[0] + initialMapBounds.northEast[0]) / 2,
      (initialMapBounds.southWest[1] + initialMapBounds.northEast[1]) / 2,
    ),
    zoom: Math.min(horizontalZoom, verticalZoom, initialMaximumZoom),
    bearing: 0,
    pitch: 0,
  });
}

interface AltitudeConversionInput {
  readonly latitude: number;
  readonly pitch: number;
  readonly viewportHeight: number;
}

function conversionScale({ latitude, pitch, viewportHeight }: AltitudeConversionInput): number {
  requireRange(latitude, -maximumMercatorLatitude, maximumMercatorLatitude, 'latitude');
  requireRange(pitch, 0, maximumPitch, 'pitch');
  requirePositive(viewportHeight, 'viewport height');
  return (
    (earthCircumference *
      Math.cos((latitude * Math.PI) / 180) *
      cameraFactor *
      viewportHeight *
      Math.cos((pitch * Math.PI) / 180)) /
    tileSize
  );
}

export function zoomToAltitude(input: AltitudeConversionInput & { readonly zoom: number }): number {
  requireRange(input.zoom, 0, 22, 'zoom');
  return conversionScale(input) / 2 ** input.zoom;
}

export function altitudeToZoom(
  input: AltitudeConversionInput & { readonly altitude: number },
): number {
  requirePositive(input.altitude, 'altitude');
  return Math.min(Math.max(Math.log2(conversionScale(input) / input.altitude), 0), 22);
}

export function lv95ToWgs84(easting: number, northing: number): LngLat {
  requireRange(easting, 2_400_000, 2_900_000, 'LV95 easting');
  requireRange(northing, 1_000_000, 1_400_000, 'LV95 northing');
  const y = (easting - 2_600_000) / 1_000_000;
  const x = (northing - 1_200_000) / 1_000_000;
  const longitudeSeconds =
    2.677_909_4 + 4.728_982 * y + 0.791_484 * y * x + 0.130_6 * y * x * x - 0.043_6 * y * y * y;
  const latitudeSeconds =
    16.902_389_2 +
    3.238_272 * x -
    0.270_978 * y * y -
    0.002_528 * x * x -
    0.044_7 * y * y * x -
    0.014 * x * x * x;
  return createLngLat((longitudeSeconds * 100) / 36, (latitudeSeconds * 100) / 36);
}
